import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";

/* Public (unauthenticated) page for requesting a password-reset email.
 * The backend always returns 204 — we never reveal whether the entered
 * username/email exists, so this page shows the same success-ish state
 * regardless of input. The user finds out via email (or its absence). */

export default function ForgotPassword() {
  const [needle, setNeedle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/v1/auth/forgot-password", { username_or_email: needle.trim() });
    } catch {
      /* Even on error, show the same generic success — no oracle. */
    }
    setBusy(false);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Reset your password</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Enter your username or email. If we find a matching account, you'll receive a reset
          link that expires in 1 hour.
        </p>

        {submitted ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-900 dark:text-blue-200">
              If an account matches, a reset link is on its way. Check your inbox (and spam
              folder).
            </div>
            <Link
              to="/login"
              className="block text-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              ← Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username or email
              </label>
              <input
                type="text"
                value={needle}
                onChange={(e) => setNeedle(e.target.value)}
                required
                autoFocus
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !needle.trim()}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <Link
              to="/login"
              className="block text-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              ← Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
